import {
  containerSizesByProfile,
  fetchApp,
  fetchService,
  getContainerProfileFromType,
  scaleService,
  selectActiveMountPointsByServiceId,
  selectAppById,
  selectContainerProfilesForStack,
  selectEndpointsByServiceId,
  selectEnvironmentById,
  selectManualScaleRecommendationByServiceId,
  selectPersistentDiskCostByService,
  selectServiceById,
} from "@app/deploy";
import { useLoader, useLoaderSuccess, useQuery, useSelector } from "@app/react";
import { appActivityUrl } from "@app/routes";
import { DEFAULT_INSTANCE_CLASS } from "@app/schema";
import type { InstanceClass } from "@app/types";
import { type SyntheticEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useValidator } from "../hooks";
import {
  Banner,
  ContainerEfficiencySection,
  ServicePricingCalc,
} from "../shared";
import {
  BannerMessages,
  Box,
  Button,
  FormGroup,
  Input,
  Select,
  type SelectOption,
} from "../shared";

const validators = {
  containerCount: (data: AppScaleProps) => {
    if (data.containerCount > 32) {
      return "Container count cannot exceed 32";
    }
  },
};

type AppScaleProps = {
  containerCount: number;
};

export const AppDetailServiceScaleV2ManualPage = () => {
  const navigate = useNavigate();
  const { id = "", serviceId = "" } = useParams();
  const [errors] = useValidator<AppScaleProps, typeof validators>(validators);
  useQuery(fetchApp({ id }));
  const [containerCount, setContainerCount] = useState(1);
  const [containerProfileType, setContainerProfileType] =
    useState<InstanceClass>(DEFAULT_INSTANCE_CLASS);
  const [containerSize, setContainerSize] = useState<number>(512);
  const [, setTakingRecommendation] = useState(false);
  const app = useSelector((s) => selectAppById(s, { id }));
  useQuery(fetchService({ id: serviceId }));
  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: app.environmentId }),
  );
  const containerProfilesForStack = useSelector((s) =>
    selectContainerProfilesForStack(s, { id: environment.stackId }),
  );
  const endpoints = useSelector((s) =>
    selectEndpointsByServiceId(s, { serviceId }),
  );
  const recommendation = useSelector((s) =>
    selectManualScaleRecommendationByServiceId(s, { serviceId: serviceId }),
  );
  const activeDiskMountPoints = useSelector((s) =>
    selectActiveMountPointsByServiceId(s, { serviceId }),
  );
  const hasDiskAttached = activeDiskMountPoints.length > 0;
  const persistentDiskCost = useSelector((s) =>
    selectPersistentDiskCostByService(s, { serviceId }),
  );

  const onSubmitForm = (e: SyntheticEvent) => {
    // Manual scaling is disallowed on v2 for now: show pending changes but
    // never dispatch. preventDefault still needed or the browser reloads.
    e.preventDefault();
  };
  const loader = useLoader(scaleService);

  useEffect(() => {
    setContainerCount(service.containerCount);
  }, [service.containerCount]);

  useEffect(() => {
    setContainerProfileType(service.instanceClass);
  }, [service.instanceClass]);

  useEffect(() => {
    if (service.containerMemoryLimitMb) {
      setContainerSize(service.containerMemoryLimitMb);
    }
  }, [service.containerMemoryLimitMb]);

  const changesExist =
    service.containerCount !== containerCount ||
    service.instanceClass !== containerProfileType ||
    service.containerMemoryLimitMb !== containerSize;

  useLoaderSuccess(loader, () => {
    navigate(appActivityUrl(app.id));
  });
  const currentContainerProfile = getContainerProfileFromType(
    service.instanceClass,
  );
  const requestedContainerProfile =
    getContainerProfileFromType(containerProfileType);

  const profileOptions = Object.keys(containerProfilesForStack).map(
    (profileType) => {
      const profile = getContainerProfileFromType(profileType as InstanceClass);
      return {
        label: profile.name,
        value: profileType,
      };
    },
  );

  const memoryContainerOptions = containerSizesByProfile(
    containerProfileType,
  ).map((containerSizeOption) => {
    return {
      label: `${containerSizeOption / 1024} GB`,
      value: `${containerSizeOption}`,
    };
  });

  const changeContainerProfile = (opt: SelectOption | null) => {
    if (!opt) return;
    const instClass = opt.value as InstanceClass;
    const profile = containerProfilesForStack[instClass];
    if (!profile) {
      return;
    }

    if (containerSize < profile.minimumContainerSize) {
      setContainerSize(profile.minimumContainerSize);
    }
    setContainerProfileType(instClass);
  };

  const cancelScale = () => {
    setContainerProfileType(service.instanceClass);
    setContainerSize(service.containerMemoryLimitMb);
    setContainerCount(service.containerCount);
    setTakingRecommendation(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <ContainerEfficiencySection
        serviceId={serviceId}
        onAcceptRecommendation={() => {
          setContainerProfileType(
            recommendation.recommendedInstanceClass as InstanceClass,
          );
          setContainerSize(recommendation.recommendedContainerMemoryLimitMb);
          setTakingRecommendation(true);
        }}
      />

      <Box>
        <form onSubmit={onSubmitForm}>
          <div className="flex flex-col gap-2">
            <h1 className="text-lg text-gray-500 mb-4">Manual Scale</h1>

            <FormGroup
              splitWidthInputs
              description="Optimize container performance with a custom profile."
              label="Container Profile"
              htmlFor="container-profile"
            >
              <Select
                id="container-profile"
                ariaLabel="container-profile"
                disabled={Object.keys(containerProfilesForStack).length <= 1}
                value={containerProfileType}
                onSelect={changeContainerProfile}
                options={profileOptions}
              />
            </FormGroup>
            <FormGroup
              splitWidthInputs
              description={`Horizontally scale this service by increasing the number of containers. A count of 2 or more will provide High Availability. 32 max count for ${
                getContainerProfileFromType(containerProfileType).name
              } profiles.`}
              label="Number of Containers"
              htmlFor="number-containers"
              feedbackMessage={errors.containerCount}
              feedbackVariant={errors.containerCount ? "danger" : "info"}
            >
              <Input
                id="number-containers"
                name="number-containers"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={containerCount}
                min="0"
                max="32"
                placeholder="0 (Min), 32 (Max)"
                onChange={(e) =>
                  setContainerCount(
                    Number.parseInt(e.currentTarget.value || "0", 10),
                  )
                }
              />
            </FormGroup>

            {hasDiskAttached && containerCount > 1 ? (
              <Banner variant="error">
                Cannot scale to more than 1 container when a disk is attached.
                Detach the disk to proceed.
              </Banner>
            ) : null}
            <FormGroup
              splitWidthInputs
              description="Specify the memory you wish to allow per container."
              label="Memory per Container"
              htmlFor="memory-container"
            >
              <Select
                id="memory-container"
                value={containerSize.toString()}
                onSelect={(opt) => setContainerSize(Number.parseInt(opt.value))}
                options={memoryContainerOptions}
              />
            </FormGroup>
            <FormGroup
              splitWidthInputs
              description="CPU Share is determined by the selected Memory Limit and Container Profile."
              label="CPU Share per Container"
              htmlFor="cpu-share"
            >
              <Input
                id="number-containers"
                name="number-containers"
                className="disabled"
                type="text"
                disabled
                value={requestedContainerProfile.cpuShare * containerSize}
              />
            </FormGroup>
          </div>

          <ServicePricingCalc
            service={service}
            endpoints={endpoints}
            persistentDiskCost={persistentDiskCost}
          />

          <hr />

          {changesExist ? (
            <div className="text-md font-semibold text-gray-900 mt-4">
              Pending Changes
            </div>
          ) : null}
          {containerProfileType !== service.instanceClass ? (
            <div className="my-3">
              <div className="text-md text-gray-900">Container Profile</div>
              <p className="text-black-500">
                Changed from {currentContainerProfile.name} to{" "}
                {requestedContainerProfile.name}
              </p>
            </div>
          ) : null}
          {containerCount !== service.containerCount ? (
            <div className="my-3">
              <div className="text-md text-gray-900">Container Count</div>
              <p className="text-black-500">
                Changed from {service.containerCount} to {containerCount}
              </p>
            </div>
          ) : null}
          {containerSize !== service.containerMemoryLimitMb ? (
            <div className="my-3">
              <div className="text-md text-gray-900">Container Size</div>
              <p className="text-black-500" id="container-size-txt">
                Changed from {service.containerMemoryLimitMb / 1024} GB to{" "}
                {containerSize / 1024} GB
              </p>
            </div>
          ) : null}
          {changesExist ? (
            <ServicePricingCalc
              service={{
                containerCount,
                containerMemoryLimitMb: containerSize,
                instanceClass: containerProfileType,
              }}
              endpoints={endpoints}
              persistentDiskCost={persistentDiskCost}
            />
          ) : null}

          <BannerMessages {...loader} />

          <div className="flex mt-4">
            <Button
              className="w-40 flex font-semibold"
              type="submit"
              disabled={!changesExist}
              isLoading={loader.isLoading}
            >
              Save Changes
            </Button>

            {changesExist ? (
              <Button
                className="w-40 ml-2 flex font-semibold"
                onClick={cancelScale}
                variant="white"
              >
                Cancel
              </Button>
            ) : null}
          </div>
        </form>
      </Box>
    </div>
  );
};
